'use client';

import { CircleLoader, CircleLoaderSize } from '@/components/ui/loaders/circle-loader/CircleLoader';
import { Colors } from '@/lib/colorUtils';
import { cn } from '@/lib/styleUtils';

import {
  brandedSurface,
  brandedStage,
  brandedGlow,
  brandedRing,
  brandedLogo,
  brandedSpinner,
  brandedMessage,
} from './BrandedLoader.style';
import type { BrandedLoaderProps } from './BrandedLoader.types';

// Three rings, staggered, so the glow keeps pulsing outward for the whole wait.
const RING_DELAYS = [
  'tw:[animation-delay:0ms]',
  'tw:[animation-delay:450ms]',
  'tw:[animation-delay:900ms]',
];

/**
 * Full-surface branded loading screen for the QR and Touchless flows.
 *
 * ## Invariants:
 * - The fill comes from the `bg-primary` token, never a prop. Why: the server writes the tenant's
 *   colour variables onto `<html>` during SSR, so the token is correct on the first painted frame
 *   with no JavaScript. Threading a colour through props would make the fill depend on the store,
 *   which is not populated until after hydration — turning a solved problem back into a flash.
 * - Renders complete without `logoSrc`, substituting the spinner in the mark's position. Why: the
 *   logo travels through the theme store, and when the server-side theme fetch fails nothing
 *   retries it, so a logo-less render is permanent rather than a frame or two. Treating the logo
 *   as required would leave those tenants staring at a hole in the layout.
 * - Presentation only — it never reads the theme store itself. Callers resolve `logoSrc` through
 *   `resolveBrandLogoSrc` so this stays usable from any surface, including tests.
 */
export const BrandedLoader = ({
  logoSrc,
  companyName,
  message,
  className,
  'data-cy': dataCy = 'branded-loader',
}: BrandedLoaderProps) => (
  <output
    className={cn(brandedSurface, className)}
    aria-busy="true"
    aria-label={message}
    data-cy={dataCy}
  >
    <div className={brandedStage}>
      <span className={brandedGlow} aria-hidden="true" />
      {RING_DELAYS.map((delay) => (
        <span key={delay} className={cn(brandedRing, delay)} aria-hidden="true" />
      ))}

      {logoSrc ? (
        /* eslint-disable-next-line @next/next/no-img-element -- the tenant logo is a runtime base64 data URL, not a static asset next/image can optimise */
        <img
          src={logoSrc}
          alt={companyName ?? ''}
          className={brandedLogo}
          data-cy="branded-loader-logo"
        />
      ) : (
        <CircleLoader
          size={CircleLoaderSize.large}
          color={Colors.colorBrandWhite}
          className={brandedSpinner}
          data-cy="branded-loader-spinner"
        />
      )}
    </div>

    {message && (
      <p className={brandedMessage} data-cy="branded-loader-message">
        {message}
      </p>
    )}
  </output>
);

BrandedLoader.displayName = 'BrandedLoader';
